import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import intl from 'react-intl-universal';
import { useHistory } from 'react-router';
import { store } from 'state/store';
import { getAllUsers } from 'state/UsersManagement/usersManagementSlice';
import { addProjectTemplate } from 'state/ProjectTemplates/projectTemplatesSlice';
import { setAssignedTeams } from 'state/ProjectTemplate/ProjectTemplateSlice';
import { showNotificationBanner } from 'state/InlineNotification/inlineNotificationSlice';
import { showNotification as showSnackbarNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import {
  PATHS,
  SETTINGS_TABS,
  SETTINGS_SECTIONS,
  SETTINGS_ATTRIBUTES,
} from 'utils/constants';
import { ProjectTemplateStatus } from 'utils/types/templates';
import useTemplateForm from '../../hooks/useTemplateForm';
import PageTitle from 'Molecules/PageTitle/PageTitle';
import DetailsPage from 'Molecules/DetailsPage/DetailsPage';
import TemplateContentTabs from '../TemplatePage/Form/TemplateContentTabs';
import TopBar from '../TemplatePage/Form/TopBar';
import PageFooter from '../TemplatePage/Form/PageFooter';
import UnsavedChangesPrompt from 'Organisms/UnsavedChangesPrompt/UnsavedChangesPrompt';

const NewTemplatePage = () => {
  const dispatch = useDispatch() as typeof store.dispatch;
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const { templateData, handleFieldChange, canSubmitForm, hasChanges } =
    useTemplateForm();

  useEffect(() => {
    dispatch(getAllUsers());
  }, [dispatch]);

  const returnToTablePage = () =>
    history.push(
      `${PATHS.SETTINGS}/${SETTINGS_TABS.CONFIGURATIONS}?section=${SETTINGS_SECTIONS.PROJECTS}&attribute=${SETTINGS_ATTRIBUTES.TEMPLATES}`
    );

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const result = await dispatch(
        addProjectTemplate({ template: templateData })
      ).unwrap();
      if (result?.id && selectedTeamIds.length > 0) {
        try {
          await dispatch(
            setAssignedTeams({
              templateId: result.id,
              teamIds: selectedTeamIds,
              previousTeamIds: [],
            })
          ).unwrap();
        } catch {
          dispatch(
            showSnackbarNotification({
              notificationVariant: 'error',
              notificationTitle: intl.get('ERROR_SNACKBAR_TITLE'),
              notificationMessage: intl.get(
                'SETTINGS_PAGE.TEMPLATE_PAGE.TEAM_ASSIGNMENT_ERROR'
              ),
            })
          );
        }
      }
      dispatch(
        showNotificationBanner({
          notificationVariant: 'success',
          notificationText: intl.get(
            'SETTINGS_PAGE.TEMPLATE_PAGE.SUCCESS_MESSAGE',
            { action: intl.get('SETTINGS_PAGE.TEMPLATE_PAGE.ACTIONS.ADD') }
          ),
        })
      );
      returnToTablePage();
    } catch {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='h-full flex flex-col shadow-skim'>
      <PageTitle
        titleComponent={intl.get('SETTINGS_PAGE.TEMPLATE_PAGE.ADD_TEMPLATE')}
      />
      <DetailsPage
        bodyClassName='overflow-y-hidden'
        topBar={
          <TopBar
            status={templateData.status}
            onUpdateStatus={(status: ProjectTemplateStatus) =>
              handleFieldChange('status', status)
            }
          />
        }
        content={
          <TemplateContentTabs
            templateData={templateData}
            updateTemplateData={handleFieldChange}
            assignedTeamIds={selectedTeamIds}
            onAssignedTeamsChange={setSelectedTeamIds}
          />
        }
      />
      <PageFooter
        canSave={canSubmitForm}
        onCancel={returnToTablePage}
        onSave={handleSave}
        isLoading={isSubmitting}
      />
      <UnsavedChangesPrompt
        hasChanges={(hasChanges || selectedTeamIds.length > 0) && !isSubmitting}
      />
    </div>
  );
};

export default NewTemplatePage;
